import { useState } from 'react';
import { 
  ChevronRight, ChevronLeft, Check, Palette, Layout, 
  Layers, Grid3X3, Sparkles, ArrowRight 
} from 'lucide-react';

interface WizardStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface ThemeWizardProps {
  onComplete: () => void;
  onStepChange: (step: string) => void;
  currentStep?: string;
}

const WIZARD_STEPS: WizardStep[] = [
  {
    id: 'templates',
    title: 'Escolha um Template',
    description: 'Comece com um template pré-configurado que combine com seu negócio',
    icon: <Palette className="w-5 h-5" />,
  },
  {
    id: 'layout',
    title: 'Defina o Layout',
    description: 'Escolha como o menu e a navegação serão exibidos',
    icon: <Layout className="w-5 h-5" />,
  },
  {
    id: 'components',
    title: 'Personalize Componentes',
    description: 'Ajuste o estilo de botões, cards e tabelas',
    icon: <Layers className="w-5 h-5" />,
  },
  {
    id: 'widgets',
    title: 'Configure Widgets',
    description: 'Escolha quais informações aparecem no dashboard',
    icon: <Grid3X3 className="w-5 h-5" />,
  },
];

export function ThemeWizard({ onComplete, onStepChange, currentStep }: ThemeWizardProps) {
  const [activeStepIndex, setActiveStepIndex] = useState(
    currentStep ? WIZARD_STEPS.findIndex(s => s.id === currentStep) : 0
  );
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const activeStep = WIZARD_STEPS[activeStepIndex];
  const isLastStep = activeStepIndex === WIZARD_STEPS.length - 1;
  const isFirstStep = activeStepIndex === 0;

  const handleNext = () => {
    // Marca o passo atual como completo
    setCompletedSteps(prev => new Set([...prev, activeStep.id]));
    
    if (isLastStep) {
      onComplete();
    } else {
      const nextIndex = activeStepIndex + 1;
      setActiveStepIndex(nextIndex);
      onStepChange(WIZARD_STEPS[nextIndex].id);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      const prevIndex = activeStepIndex - 1;
      setActiveStepIndex(prevIndex);
      onStepChange(WIZARD_STEPS[prevIndex].id);
    }
  };

  const handleStepClick = (index: number) => {
    setActiveStepIndex(index);
    onStepChange(WIZARD_STEPS[index].id);
  };

  return (
    <div className="bg-gradient-to-r from-primary-900/30 to-accent-900/30 rounded-xl p-4 mb-6 border border-primary-500/20">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-4">
        {WIZARD_STEPS.map((step, index) => {
          const isActive = index === activeStepIndex;
          const isCompleted = completedSteps.has(step.id);
          const isPast = index < activeStepIndex;
          
          return (
            <div key={step.id} className="flex items-center flex-1">
              <button
                onClick={() => handleStepClick(index)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg transition-all
                  ${isActive 
                    ? 'bg-primary-500/20 text-primary-400' 
                    : isCompleted || isPast
                      ? 'text-green-400 hover:bg-surface-800'
                      : 'text-surface-400 hover:bg-surface-800'
                  }
                `}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${isActive 
                    ? 'bg-primary-500 text-white' 
                    : isCompleted || isPast
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-surface-700 text-surface-400'
                  }
                `}>
                  {isCompleted || isPast ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium">{step.title}</div>
                </div>
              </button>
              
              {index < WIZARD_STEPS.length - 1 && (
                <div className={`
                  flex-1 h-0.5 mx-2
                  ${isPast || isCompleted ? 'bg-green-500/50' : 'bg-surface-700'}
                `} />
              )}
            </div>
          );
        })}
      </div>

      {/* Current Step Info */}
      <div className="flex items-center justify-between bg-surface-800/50 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary-500/20 rounded-xl">
            {activeStep.icon}
          </div>
          <div>
            <h3 className="font-semibold text-white">{activeStep.title}</h3>
            <p className="text-sm text-surface-400">{activeStep.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isFirstStep && (
            <button
              onClick={handlePrevious}
              className="flex items-center gap-1 px-3 py-2 text-surface-300 hover:text-white hover:bg-surface-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex items-center gap-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 transition-colors"
          >
            {isLastStep ? (
              <>
                <Sparkles className="w-4 h-4" />
                Finalizar
              </>
            ) : (
              <>
                Próximo
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="mt-3 flex items-center gap-2 text-xs text-surface-400">
        <Sparkles className="w-3 h-3 text-primary-400" />
        <span>
          {activeStepIndex === 0 && 'Dica: Escolha um template que combine com o segmento do seu negócio'}
          {activeStepIndex === 1 && 'Dica: O layout Sidebar é mais comum para aplicações desktop'}
          {activeStepIndex === 2 && 'Dica: Botões Gradient chamam mais atenção para ações principais'}
          {activeStepIndex === 3 && 'Dica: Menos widgets = dashboard mais limpo e rápido'}
        </span>
      </div>
    </div>
  );
}

// Componente para mostrar/esconder o wizard
export function WizardToggle({ 
  showWizard, 
  onToggle 
}: { 
  showWizard: boolean; 
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className={`
        flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
        ${showWizard 
          ? 'bg-primary-500/20 text-primary-400' 
          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
        }
      `}
    >
      <Sparkles className="w-4 h-4" />
      {showWizard ? 'Modo Guiado Ativo' : 'Ativar Modo Guiado'}
    </button>
  );
}

export default ThemeWizard;
